import type { GameMeta } from '@awwyeah/shared';

/**
 * The finished-games archive (WS-17b): grouping/sorting only, no rendering. Same month-grid
 * shape as the check-in archive (`apps/web/src/modules/checkin/feed.ts`), duplicated rather
 * than imported — feature modules don't reach into each other's internals
 * (docs/ARCHITECTURE.md; the same precedent `useHouseholdRoster.ts` already notes).
 *
 * **No-demand-mechanics compliance (memory/no-demand-mechanics.md):** this file computes
 * exactly one thing about a finished game — the game itself (who played, who won, when,
 * how many moves). It deliberately has no function anywhere that aggregates across games:
 * no win/loss tally, no streak, no "Dan leads 5-3". Each finished game is its own memory,
 * full stop. If the owner ever wants a tally, he'll ask for it explicitly — don't add one
 * preemptively just because the data is sitting right here.
 */

export interface GameMonthGroup {
  /** `YYYY-MM` — stable key and sort value. */
  key: string;
  label: string;
  games: GameMeta[];
}

/** Below this many finished games, the archive is just a flat newest-first list — grouping
 * only earns its keep once there's enough history to want headers. */
export const ARCHIVE_GROUP_THRESHOLD = 8;

function finishedIso(game: GameMeta): string {
  // `finishedAt` is WS-17b; games finished before it (or, in principle, any doc written by
  // a client that raced the stamp) fall back to `updatedAt` rather than sorting as if they
  // have no date at all.
  return game.finishedAt ?? game.updatedAt;
}

function monthKeyOf(game: GameMeta): string {
  return finishedIso(game).slice(0, 7);
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(year ?? 1970, (month ?? 1) - 1, 1),
  );
}

/** Newest-finished first; ties broken by id so rendering never shuffles between renders. */
export function sortFinishedNewestFirst(games: readonly GameMeta[]): GameMeta[] {
  return [...games].sort((a, b) => {
    const diff = Date.parse(finishedIso(b)) - Date.parse(finishedIso(a));
    return diff !== 0 ? diff : b.id.localeCompare(a.id);
  });
}

/** Groups finished games by the month they finished in, newest month first. */
export function groupGamesByMonth(games: readonly GameMeta[]): GameMonthGroup[] {
  const buckets = new Map<string, GameMeta[]>();
  for (const game of sortFinishedNewestFirst(games)) {
    const key = monthKeyOf(game);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(game);
    else buckets.set(key, [game]);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, items]) => ({ key, label: monthLabel(key), games: items }));
}

/** "Dan won" / "Draw" — a fact, never a record. `winnerName` is already resolved by the
 * caller (this module doesn't know about the roster). */
export function outcomeLabel(game: GameMeta, winnerName: string | null): string {
  if (!game.winnerId) return 'Draw';
  return `${winnerName ?? 'Someone'} won`;
}

/** "Aug 3" — the archive card's date-finished line. */
export function finishedDateLabel(game: GameMeta): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
    new Date(finishedIso(game)),
  );
}
